import InstagramIcon from '../../asset/image/icon/instagram.png'
import { useSessionContext } from '../../context/session-context'
import { useTrainerModalContext } from '../../context/trainer-modal-context'
import { ImagePublicFileId } from '../image/image-public-file-id'
import { Spinner } from '../util/spinner/spinner'
import React, { useEffect, useMemo } from 'react'
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle } from 'src/component/ui/dialog'
import { Trainer } from 'src/dao/core-dao'

// Helen and Jay
const noInstagramTrainedIds = new Set(['8b64c42f-43f7-47af-9550-0a5ab9355725', 'c7ac4cba-bc18-4452-95c0-a9bdbf560522'])

export const TrainerDetails = (): React.ReactElement => {
  const navigate = useNavigate()
  const location = useLocation()
  // URL logic supports direct visits (bookmarks, shared links) to /trainers/:trainerId
  const { trainerId } = useParams<{ trainerId: string }>()
  const match = useMatch('/trainers/:trainerId')
  const { trainerById, trainers } = useSessionContext()
  const { openTrainerId, trainerFromContext, closeTrainer } = useTrainerModalContext()

  const isOpenFromContext = openTrainerId !== null
  const isOpenFromUrl = !!(match && trainerId)

  const trainerFromState = (location.state as { trainer?: Trainer })?.trainer
  const trainerFromContextResolved = isOpenFromContext
    ? trainerFromContext ?? (openTrainerId ? trainerById(openTrainerId) : undefined)
    : undefined
  const trainerFromUrl = isOpenFromUrl ? trainerFromState ?? (trainerId ? trainerById(trainerId) : undefined) : undefined
  const trainer = trainerFromContextResolved ?? trainerFromUrl

  const isLoading =
    (isOpenFromContext && !trainer && trainers === undefined) ||
    (isOpenFromUrl && !isOpenFromContext && !trainer && trainers === undefined)

  useEffect(() => {
    if (isOpenFromUrl && !isOpenFromContext && trainerId && !trainer && trainers !== undefined) {
      navigate('/', { replace: true })
    }
  }, [isOpenFromUrl, isOpenFromContext, trainerId, trainer, trainers, navigate])

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      if (isOpenFromContext) {
        closeTrainer()
      } else if (isOpenFromUrl) {
        navigate('/', { replace: true })
      }
    }
  }

  const isOpen = isOpenFromContext || isOpenFromUrl

  return (
    <Dialog open={isOpen && (!!trainer || isLoading)} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">
          {trainer ? `Meet ${trainer.firstName} ${trainer.lastName}` : 'Trainer Profile'}
        </DialogTitle>
        {trainer && <TrainerDetailsContent trainer={trainer} />}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Spinner />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

const TrainerDetailsContent = (props: { trainer: Trainer }): React.ReactElement => {
  const { trainer } = props

  const trainerInstagramUrl = useMemo((): string | undefined => {
    if (noInstagramTrainedIds.has(trainer.id)) {
      return undefined
    }
    return trainer.socialProfiles?.['instagram']
  }, [trainer])

  return (
    <>
      <div className="mb-3">
        <div className="flex flex-col items-center w-full">
          <div className="w-[187px] h-[187px] mb-9">
            <ImagePublicFileId className="w-full h-full object-cover rounded-full" fileId={trainer.profilePhotoPublicFileId} />
          </div>
        </div>
        <h1 className="text-ivory font-evogria text-xl text-center tracking-wide leading-tight">
          MEET {trainer.firstName.toUpperCase()} {trainer.lastName.toUpperCase()}
        </h1>
      </div>
      <div className="flex items-center justify-center w-full mb-2">
        {trainerInstagramUrl && (
          <a
            href={trainerInstagramUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center rounded-full transition-transform duration-200 hover:scale-110 no-underline"
          >
            <img className="w-[24px] h-[24px]" src={InstagramIcon} alt="instagram" />
          </a>
        )}
      </div>

      <div className="w-full max-w-[550px] text-left">
        {trainer.description.split('\n').map((paragraph, ix) => (
          <p key={ix} className="text-mist font-roboto-condensed text-sm leading-relaxed mb-3 last:mb-0">
            {paragraph}
          </p>
        ))}
      </div>
    </>
  )
}
